import React, { useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Routes } from 'constants/index';
import { useHistory } from 'react-router';
import { getPath } from 'utils';
import { Button, Loader } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { personAccountIdSelector } from 'modules/user/selectors';
import {
  educationalQualificationDataSelector,
  professionalQualificationDataSelector,
  workExperienceDataSelector,
} from 'modules/personLevelExemption';
import { useSaveExemptionCalculatorData } from 'hooks/useSaveExemptionCalculatorData';
import { clickedBeginApplicationSelector } from 'modules/membership/selectors';

export const ExemptionCalculatorResult = () => {
  const personAccountId = useSelector(personAccountIdSelector);
  const professionalBodyQualifications = useSelector(professionalQualificationDataSelector);
  const educationalQualifications = useSelector(educationalQualificationDataSelector);
  const workExperienceQualifications = useSelector(workExperienceDataSelector);
  const isBeginApplicationClicked = useSelector(clickedBeginApplicationSelector);

  const {
    isLoading = false,
    hasError = false,
    isQualificationsIncomplete = false,
  } = useSaveExemptionCalculatorData(
    personAccountId,
    professionalBodyQualifications,
    educationalQualifications,
    workExperienceQualifications
  );

  const history = useHistory();

  const handleHomeClick = useCallback(() => {
    history.push(getPath(Routes.ROOT));
  }, [history]);

  const handleBeginApplicationClick = useCallback(() => {
    history.push(getPath(Routes.CIMA_MEMBERSHIP_PROMO_CODE));
  }, [history]);

  // redirection
  useEffect(() => {
    // redirect to Professional Qualification Form if page is refreshed or if user jumped to Results Page
    if (isQualificationsIncomplete) {
      history.push(getPath(Routes.EXEMPTION_CALCULATOR_PROFESSIONAL));
    }
    // redirect to begin application if Begin Application is clicked from summary page
    if (isBeginApplicationClicked && !isLoading) {
      history.push(getPath(Routes.CIMA_MEMBERSHIP_PROMO_CODE));
    }
  }, [isQualificationsIncomplete, isLoading, isBeginApplicationClicked, history]); // eslint-disable-next-line react-hooks/exhaustive-deps

  if (isLoading || isBeginApplicationClicked) return <Loader active content="Loading" />;
  return (
    <>
      <StyledBody>
        {hasError ? (
          <StyledText>Sorry, something went wrong, please try again.</StyledText>
        ) : (
          <>
            <StyledText>Your data is saved!</StyledText>
            <StyledSubtext>
              You can now continue with the application process or exit the screen and comeback later.
            </StyledSubtext>
          </>
        )}

        <StyledButtonContainer>
          {!hasError && <StyledBeginButton onClick={handleBeginApplicationClick}>Begin Application</StyledBeginButton>}
          <StyledHomeButton onClick={handleHomeClick}>Go back to homepage</StyledHomeButton>
        </StyledButtonContainer>
      </StyledBody>
    </>
  );
};

const StyledText = styled.p`
  text-align: center;
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const StyledSubtext = styled.p`
  text-align: center;
  font-size: ${props => props.theme.fontSizes.s};
`;

const BaseStyles = css`
  max-width: 930px;
  margin: auto;
  line-height: 1.5;
  width: 100%;
`;

const StyledBody = styled.div`
  ${BaseStyles}
  padding-top: 24px;
  color: ${props => props.theme.colors.neutralGrey8};
  font-size: ${props => props.theme.pxToRem(16)};
  height: 750px;

  ${({ theme }) => theme.mediaQueries.mobileOnly} {
    padding: 64px 20px;
    margin: 0px;
  }
`;

const StyledButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: ${props => props.theme.pxToRem(50)};
`;

const StyledBeginButton = styled(Button)`
  &&&& {
    height: ${props => props.theme.pxToRem(50)};
    width: ${props => props.theme.pxToRem(270)};
    margin: 0 auto;
    margin-bottom: 8px;
    border: solid ${props => props.theme.pxToRem(1)} ${props => props.theme.colors.primaryLightPurple};
    background-color: ${props => props.theme.colors.primaryLightPurple};
    color: ${props => props.theme.colors.neutralWhite};
    margin-top: ${props => props.theme.pxToRem(15)};
  }
`;

const StyledHomeButton = styled(Button)`
  &&&& {
    height: ${props => props.theme.pxToRem(50)};
    width: ${props => props.theme.pxToRem(270)};
    margin: 0 auto;
    margin-bottom: 8px;
    border: solid ${props => props.theme.pxToRem(1)} ${props => props.theme.colors.primaryLightPurple};
    background-color: ${props => props.theme.colors.neutralWhite};
    color: ${props => props.theme.colors.primaryLightPurple};
    margin-top: ${props => props.theme.pxToRem(15)};
  }
`;
